import classNames from "classnames";
import type { MouseEvent } from "react";
import { memo } from "react";
import * as redoTableHeaderCellsCss from "./redo-table-header-cells.module.css";

export const MINIMUM_COLUMN_WIDTH = 45;

export function getResizedColumnWidth({
  currentWidth,
  widthDif,
}: {
  currentWidth: number;
  widthDif: number;
}): number {
  return Math.max(MINIMUM_COLUMN_WIDTH, currentWidth + widthDif);
}

export interface RedoTableColumnResizerProps {
  held: boolean;
  onAdjustColumnWidth: ({ widthDif }: { widthDif: number }) => void;
  onResizeStart?: ({ width }: { width: number }) => void;
  setHeld: (held: boolean) => void;
  showSeparator?: boolean;
}

export const RedoTableColumnResizer = memo(function RedoTableColumnResizer({
  held,
  onAdjustColumnWidth,
  onResizeStart,
  setHeld,
  showSeparator = false,
}: RedoTableColumnResizerProps) {
  const handler = (mouseDownEvent: MouseEvent<HTMLDivElement>) => {
    if (mouseDownEvent.button !== 0) {
      return;
    }
    mouseDownEvent.preventDefault();
    mouseDownEvent.stopPropagation();

    const separatorRect = mouseDownEvent.currentTarget.getBoundingClientRect();
    const columnRect =
      mouseDownEvent.currentTarget.parentElement?.getBoundingClientRect();
    const initialOffsetToSeparator =
      mouseDownEvent.clientX - (separatorRect.left + separatorRect.width / 2);
    const startX = mouseDownEvent.pageX - initialOffsetToSeparator;
    const previousBodyCursor = document.body.style.cursor;
    const previousBodyUserSelect = document.body.style.userSelect;
    let rightColumnExpandInterval: ReturnType<typeof setInterval> | null = null;
    let currentWidthDif = 0;
    let mouseDisplacementX = 0;
    let didResize = false;

    onResizeStart?.({ width: columnRect?.width ?? 0 });
    setHeld(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function startRightColumnExpandInterval() {
      if (rightColumnExpandInterval) {
        return;
      }
      rightColumnExpandInterval = setInterval(() => {
        currentWidthDif += 5;
        didResize = true;
        onAdjustColumnWidth({ widthDif: mouseDisplacementX + currentWidthDif });
      }, 25);
    }

    function clearRightColumnExpandInterval() {
      if (rightColumnExpandInterval) {
        clearInterval(rightColumnExpandInterval);
        rightColumnExpandInterval = null;
      }
    }

    function onMouseMove(mouseMoveEvent: globalThis.MouseEvent) {
      const screenWidth = window.innerWidth;
      const distanceFromRightEdge = screenWidth - mouseMoveEvent.pageX;
      if (distanceFromRightEdge < 10) {
        startRightColumnExpandInterval();
      } else {
        clearRightColumnExpandInterval();
        mouseDisplacementX =
          mouseMoveEvent.pageX - initialOffsetToSeparator - startX;
        didResize = true;
        onAdjustColumnWidth({ widthDif: mouseDisplacementX + currentWidthDif });
      }
    }

    function preventClick(clickEvent: globalThis.MouseEvent) {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    }

    function cleanup() {
      clearRightColumnExpandInterval();
      setHeld(false);
      document.body.style.cursor = previousBodyCursor;
      document.body.style.userSelect = previousBodyUserSelect;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("blur", cleanup);
    }

    function onMouseUp() {
      cleanup();
      if (didResize) {
        document.addEventListener("click", preventClick, {
          capture: true,
          once: true,
        });
        window.setTimeout(() => {
          document.removeEventListener("click", preventClick, true);
        }, 0);
      }
    }

    function onMouseLeave() {
      cleanup();
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("mouseleave", onMouseLeave, { once: true });
    window.addEventListener("blur", cleanup, { once: true });
  };

  return (
    <div
      aria-label="Resize column"
      aria-orientation="vertical"
      className={classNames(
        redoTableHeaderCellsCss.columnResizer,
        held && redoTableHeaderCellsCss.columnResizerActive,
        showSeparator && redoTableHeaderCellsCss.columnResizerVisible,
      )}
      data-table-resize-handle="true"
      data-table-resize-separator-visible={showSeparator ? "true" : undefined}
      data-table2-resize-handle="true"
      onMouseDown={handler}
      role="separator"
    />
  );
});
